import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { backupService } from '../../services/api';
import { Spinner } from '../../components/Spinner';
import { formatIST, formatDay } from '../../utils/dates';
import { ArrowDownTrayIcon, CheckCircleIcon, CloudArrowUpIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const sizeOf = (bytes) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const labelOf = (f) => (f.kind === 'monthly'
  ? `${MONTHS[Number(f.stamp.slice(5, 7)) - 1]} ${f.stamp.slice(0, 4)}`
  : formatDay(f.stamp, 'dd MMM yyyy'));

// Hands a downloaded file to the browser
const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const Backups = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isSuperAdmin = user?.role === 'super_admin';
  const { data, isLoading } = useQuery('backups', backupService.list, { enabled: isSuperAdmin, retry: false });
  const result = data?.data?.data || {};
  const files = result.files || [];
  const lastRun = result.lastRun;
  const [busyKey, setBusyKey] = useState('');

  const runNow = useMutation(backupService.runNow, {
    onSuccess: () => { toast.success('Backup saved'); queryClient.invalidateQueries('backups'); },
    onError:   (e) => toast.error(e.response?.data?.message || 'Backup failed'),
  });

  const download = async (file, format) => {
    setBusyKey(`${file.key}:${format}`);
    try {
      if (format === 'raw') {
        const res = await backupService.rawLink(file.key);
        window.location.href = res.data.data.url;
      } else {
        const res = await backupService.excel(file.key);
        saveBlob(res.data, `backup_${file.stamp}.xlsx`);
      }
    } catch (e) {
      toast.error('Download failed');
    } finally {
      setBusyKey('');
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
        Backups are available to the Super Admin of this workspace.
      </div>
    );
  }
  if (isLoading) return <div className="p-6 text-center"><Spinner size="md" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Backups</h1>
          <p className="mt-1 text-sm text-gray-500">
            Every night this workspace's leads, their history and its users are saved to secure storage.
            Daily backups are kept for {result.keep?.dailyDays || 30} days and one per month for {result.keep?.monthly || 6} months.
          </p>
        </div>
        {result.configured && (
          <button onClick={() => runNow.mutate()} disabled={runNow.isLoading} className="btn-primary text-sm flex items-center gap-2">
            {runNow.isLoading ? <Spinner size="sm" /> : <CloudArrowUpIcon className="h-4 w-4" />}
            Back up now
          </button>
        )}
      </div>

      {!result.configured ? (
        <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-3 text-sm text-amber-800 flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          Backup storage isn't connected yet. Your administrator needs to add the Cloudflare R2 settings on the server.
        </div>
      ) : lastRun && (
        <div className={`rounded-lg px-4 py-3 text-sm flex items-center gap-2 border ${
          lastRun.status === 'failed' ? 'bg-red-50 border-red-100 text-red-700' : 'bg-green-50 border-green-100 text-green-700'}`}
        >
          {lastRun.status === 'failed'
            ? <><ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" /> Last night's backup failed: {lastRun.error}</>
            : <><CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
                Last nightly backup {lastRun.status === 'running' ? 'is running' : `finished ${formatIST(lastRun.finishedAt, 'dd MMM yyyy, HH:mm')}`}
                {lastRun.counts?.leads != null && ` — ${lastRun.counts.leads.toLocaleString()} leads, ${(lastRun.counts.leadactivities || 0).toLocaleString()} activities`}
              </>}
        </div>
      )}

      {result.configured && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {files.length === 0 ? (
            <p className="p-8 text-center text-sm text-gray-400">No backups yet. The first one runs tonight after 2:30 AM, or use "Back up now".</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Backup of', 'Type', 'Size', 'Download'].map(h => (
                      <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {files.map(f => (
                    <tr key={f.key}>
                      <td className="px-4 py-2 font-medium text-gray-900 whitespace-nowrap">{labelOf(f)}</td>
                      <td className="px-4 py-2">
                        <span className={`badge ${f.kind === 'monthly' ? 'badge-primary' : 'badge-secondary'}`}>{f.kind === 'monthly' ? 'Monthly' : 'Daily'}</span>
                      </td>
                      <td className="px-4 py-2 text-gray-600">{sizeOf(f.size)}</td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-3">
                          <button onClick={() => download(f, 'excel')} disabled={!!busyKey} className="text-primary-600 hover:underline flex items-center gap-1">
                            {busyKey === `${f.key}:excel` ? <Spinner size="sm" /> : <ArrowDownTrayIcon className="h-4 w-4" />} Excel
                          </button>
                          <button onClick={() => download(f, 'raw')} disabled={!!busyKey} className="text-gray-500 hover:underline flex items-center gap-1" title="Everything in the backup, for restoring">
                            {busyKey === `${f.key}:raw` ? <Spinner size="sm" /> : <ArrowDownTrayIcon className="h-4 w-4" />} Full file
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
